import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { PhoneNumberApiService } from '../services/phone-number-api.service';
import {
  PhoneNumberRequest,
  PhoneNumberResponse,
  PhoneNumberType
} from '../models/phone-number.model';
import { PHONE_NUMBER_TYPE_LABELS } from '../call-labels';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-phone-number-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './phone-number-form.component.html'
})
export class PhoneNumberFormComponent implements OnInit {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<PhoneNumberResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  phoneNumberId: number | null = null;

  // Diccionario único del módulo (call-labels.ts): no duplicar etiquetas aquí.
  readonly numberTypeOptions: { value: PhoneNumberType; label: string }[] =
    (Object.keys(PHONE_NUMBER_TYPE_LABELS) as PhoneNumberType[]).map(value => ({
      value,
      label: PHONE_NUMBER_TYPE_LABELS[value]
    }));

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: PhoneNumberApiService,
    private readonly toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {}

  private initForm(): void {
    this.form = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\+[1-9]\d{1,14}$/)]],
      twilioSid: ['', [Validators.pattern(/^PN[a-fA-F0-9]{32}$/)]],
      numberType: ['LOCAL', [Validators.required]],
      friendlyName: ['', [Validators.maxLength(150)]],
      agentId: [null],
      teamId: [null],
      setupCost: [null],
      monthlyCost: [null],
      isActive: [true],
      isDefault: [false]
    });
  }

  reset(): void {
    this.isEditing = false;
    this.phoneNumberId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadPhoneNumber(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.phoneNumberId = id;
    this.api.getPhoneNumberById(id).subscribe({
      next: (item) => {
        this.form.patchValue({
          phoneNumber: item.phoneNumber,
          twilioSid: item.twilioSid || '',
          numberType: item.numberType,
          friendlyName: item.friendlyName || '',
          agentId: item.agentId ?? null,
          teamId: item.teamId ?? null,
          setupCost: item.setupCost ?? null,
          monthlyCost: item.monthlyCost ?? null,
          isActive: item.isActive ?? true,
          isDefault: item.isDefault ?? false
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el número telefónico';
        this.loading = false;
      }
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: PhoneNumberRequest = {
      phoneNumber: v.phoneNumber.trim(),
      numberType: v.numberType,
      friendlyName: v.friendlyName?.trim() || undefined,
      isActive: !!v.isActive,
      isDefault: !!v.isDefault
    };
    if (v.twilioSid?.trim()) body.twilioSid = v.twilioSid.trim();
    if (v.agentId != null && v.agentId !== '') body.agentId = +v.agentId;
    if (v.teamId != null && v.teamId !== '') body.teamId = +v.teamId;
    if (v.setupCost != null && v.setupCost !== '') body.setupCost = +v.setupCost;
    if (v.monthlyCost != null && v.monthlyCost !== '') body.monthlyCost = +v.monthlyCost;

    const req$ = this.isEditing && this.phoneNumberId
      ? this.api.updatePhoneNumber(this.phoneNumberId, body)
      : this.api.createPhoneNumber(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Número telefónico');
        } else {
          this.toast.created('Número telefónico');
        }
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el número telefónico';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    if (c.errors['pattern']) {
      if (field === 'phoneNumber') return 'Formato E.164 requerido (ej. +5117654321)';
      if (field === 'twilioSid') return 'Formato PN + 32 caracteres hexadecimales';
    }
    return 'Campo inválido';
  }
}
